import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { router, usePage } from "@inertiajs/react";
import { TriangleAlert, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { SharedData } from "@/types";
import { urlFor } from "~/client";
import { useTransmitChannel } from "~/hooks/use-transmit-channel";

/**
 * Aviso realtime de "te asignaron un ticket", escalado por prioridad. Payload:
 * ver `app/events/ticket_alert_broadcast.ts`. Solo lo ve el destinatario (topic
 * privado por usuario). Escalado:
 * - urgent/critical → modal encolado (de a uno), solo informa y enlaza.
 * - high → toast persistente (no se auto-cierra) con acción "Ver ticket".
 * - normal/low/desconocida → toast discreto que se auto-cierra.
 *
 * Montar una sola vez en el layout autenticado. La campana in-app + push FCM
 * siguen siendo el canal durable; esto es solo la señal en pantalla.
 */
type TicketAlertPayload = {
  event: "assigned";
  ticket_id: number;
  number_ticket: number;
  title: string;
  priority: string;
  assigned_by_name: string | null;
};

/**
 * Item encolado para el modal: lleva la URL resuelta a EVENT-time. El layout es
 * persistente y `urlFor` toma el company_slug de la URL actual: si el usuario
 * cambia de organización con el aviso todavía en pantalla, resolver a
 * click-time construiría la ruta de la empresa equivocada (numberTicket es
 * por-empresa → otro ticket o 404).
 */
type QueuedTicketAlert = TicketAlertPayload & { ticketUrl: string };

const MODAL_PRIORITIES = new Set(["urgent", "critical"]);

const PRIORITY_FALLBACK_LABELS: Record<string, string> = {
  low: "Baja",
  normal: "Normal",
  high: "Alta",
  urgent: "Urgente",
  critical: "Crítica",
};

function goToTicket(url: string) {
  router.visit(url);
}

export function TicketAssignedAlert() {
  const { t } = useTranslation("chat");
  const { auth } = usePage<SharedData>().props;
  const companyId = auth.company?.id ? Number(auth.company.id) : null;
  const currentUserId = auth.user?.id ? Number(auth.user.id) : null;

  const [modalQueue, setModalQueue] = useState<QueuedTicketAlert[]>([]);

  // Los avisos encolados pertenecen a la empresa donde llegaron: al cambiar de
  // organización se descartan (su URL/numberTicket ya no aplican; la campana
  // in-app sigue siendo el canal durable).
  useEffect(() => {
    setModalQueue([]);
  }, [companyId]);

  const handleRealtime = useCallback(
    (payload: TicketAlertPayload) => {
      if (
        !payload ||
        payload.event !== "assigned" ||
        typeof payload.ticket_id !== "number" ||
        typeof payload.number_ticket !== "number"
      ) {
        return;
      }

      // Prioridad desconocida o ausente degrada a toast discreto: el aviso
      // nunca debe romperse por un payload viejo/nuevo.
      const priority = typeof payload.priority === "string" ? payload.priority : "normal";

      // URL resuelta AHORA, con la empresa activa al llegar el evento (ver
      // QueuedTicketAlert).
      const ticketUrl = urlFor("tickets.show", { numberTicket: payload.number_ticket });

      if (MODAL_PRIORITIES.has(priority)) {
        setModalQueue((queue) =>
          queue.some((item) => item.ticket_id === payload.ticket_id)
            ? queue
            : [...queue, { ...payload, ticketUrl }],
        );
        return;
      }

      const fromLine = payload.assigned_by_name
        ? t("ticket_alerts.from", {
            name: payload.assigned_by_name,
            defaultValue: "De {{name}}",
          })
        : null;

      const persistent = priority === "high";

      if (persistent) {
        toast.custom(
          (toastId) => (
            <div className="relative flex w-[356px] max-w-[calc(100vw-2rem)] items-start gap-3 rounded-lg border bg-popover p-4 pr-10 text-popover-foreground shadow-lg">
              <div className="min-w-0 flex-1 space-y-1">
                <p className="text-sm font-semibold">
                  {t("ticket_alerts.assigned", "Te asignaron un ticket")}
                </p>
                {([payload.title, fromLine].filter(Boolean).join(" — ") || undefined) && (
                  <p className="text-sm text-muted-foreground">
                    {[payload.title, fromLine].filter(Boolean).join(" — ")}
                  </p>
                )}
              </div>
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  toast.dismiss(toastId);
                  goToTicket(ticketUrl);
                }}
              >
                {t("ticket_alerts.actions.view", "Ver ticket")}
              </Button>
              <button
                type="button"
                aria-label={t("ticket_alerts.actions.dismiss", "Entendido")}
                className="absolute right-2 top-2 rounded-md p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => toast.dismiss(toastId)}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ),
          { duration: Number.POSITIVE_INFINITY },
        );
        return;
      }

      toast.message(t("ticket_alerts.assigned", "Te asignaron un ticket"), {
        description: [payload.title, fromLine].filter(Boolean).join(" — ") || undefined,
        action: {
          label: t("ticket_alerts.actions.view", "Ver ticket"),
          onClick: () => goToTicket(ticketUrl),
        },
      });
    },
    [t],
  );

  const topic =
    companyId && currentUserId ? `/${companyId}/user/${currentUserId}/ticket-alert` : null;
  useTransmitChannel<TicketAlertPayload>(topic, handleRealtime);

  const current = modalQueue[0] ?? null;
  const dismiss = () => setModalQueue((queue) => queue.slice(1));

  if (!current) {
    return null;
  }

  const priorityLabel = t(
    `ticket_alerts.priority.${current.priority}`,
    PRIORITY_FALLBACK_LABELS[current.priority] ?? current.priority,
  );

  return (
    <Dialog open onOpenChange={(next) => (!next ? dismiss() : undefined)}>
      <DialogContent key={current.ticket_id} className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TriangleAlert className="text-destructive h-5 w-5" />
            {t("ticket_alerts.modal.title", "Te asignaron un ticket prioritario")}
          </DialogTitle>
          <DialogDescription>
            {current.assigned_by_name
              ? t("ticket_alerts.from", {
                  name: current.assigned_by_name,
                  defaultValue: "De {{name}}",
                })
              : t("ticket_alerts.from_someone", "Alguien te asignó este ticket")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <p className="text-base font-medium break-words">{current.title}</p>
          <Badge variant="destructive">{priorityLabel}</Badge>
        </div>

        <DialogFooter className="flex-row gap-2 sm:justify-between">
          <Button type="button" variant="outline" className="flex-1" onClick={dismiss}>
            {t("ticket_alerts.actions.dismiss", "Entendido")}
          </Button>
          <Button
            type="button"
            className="flex-1"
            onClick={() => {
              const target = current;
              dismiss();
              goToTicket(target.ticketUrl);
            }}
          >
            {t("ticket_alerts.actions.view", "Ver ticket")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
